/**
 * lib/attachments.js — Sprint 3: allegati effimeri one-view.
 *
 * Supporta:
 *   - Immagini (.jpg/.png/.webp) — compressione client-side a 1080px max
 *   - Video brevi (.mp4) — durata max 10s, compressione 480p
 *
 * Flow upload:
 *   1. Comprimi blob (canvas per image, MediaRecorder per video)
 *   2. Genera chiave random XChaCha20-Poly1305
 *   3. Cifra a stream
 *   4. prepareAttachmentUpload → ricevi signed URL
 *   5. Upload diretto a Storage
 *   6. sendMessage con attachmentPath, attachmentKey (cifrata con boxKey del destinatario), nonce, kind
 *
 * Flow download:
 *   1. prepareAttachmentDownload → marca viewedAt
 *   2. Scarica blob cifrato da Storage
 *   3. Decifra con chiave estratta dal messaggio
 *   4. Mostra con auto-burn dopo singola visione
 *   5. markMessageViewed → Cloud Task schedula delete preciso
 */

import sodium from 'libsodium-wrappers';
import { ref, uploadBytes, getBlob } from 'firebase/storage';
import { storage } from './firebase';

const MAX_IMAGE_DIM = 1080;
const IMAGE_QUALITY = 0.78;
const MAX_VIDEO_SECONDS = 60;

// =====================================================================
// COMPRESSION
// =====================================================================

/**
 * Comprime un'immagine al max 1080px lato lungo, jpeg 78% qualità.
 * Input: File / Blob
 * Output: Blob compresso
 */
export async function compressImage(file) {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');

  let { width, height } = img;
  if (width > MAX_IMAGE_DIM || height > MAX_IMAGE_DIM) {
    const ratio = MAX_IMAGE_DIM / Math.max(width, height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('COMPRESS_FAILED')),
      'image/jpeg',
      IMAGE_QUALITY
    );
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('IMAGE_LOAD_FAILED'));
    };
    img.src = url;
  });
}

/**
 * Genera thumbnail compressa (per video preview, max 320px).
 */
export async function generateThumbnail(videoFile) {
  const video = document.createElement('video');
  video.src = URL.createObjectURL(videoFile);
  video.muted = true;
  video.playsInline = true;

  await new Promise((resolve, reject) => {
    video.onloadeddata = resolve;
    video.onerror = reject;
  });

  // Seek a 0.1s per evitare frame neri all'inizio
  video.currentTime = 0.1;
  await new Promise((resolve) => { video.onseeked = resolve; });

  const canvas = document.createElement('canvas');
  const ratio = 320 / Math.max(video.videoWidth, video.videoHeight);
  canvas.width = Math.round(video.videoWidth * ratio);
  canvas.height = Math.round(video.videoHeight * ratio);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  URL.revokeObjectURL(video.src);

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error('THUMB_FAILED')),
      'image/jpeg',
      0.7
    );
  });
}

/**
 * Verifica durata video <= 10s. Throws se troppo lungo.
 * NB: la compressione vera richiede ffmpeg.wasm o MediaRecorder con stream.
 * In Sprint 3 v1: accettiamo i video as-is purché < 12MB e < 10s.
 * Sprint 3 v2: aggiungiamo compressione con ffmpeg.wasm.
 */
export async function validateVideo(file) {
  // Size check PRIMA (e' la causa piu' frequente di fallimento sui device
  // mobili: una clip di 20s in HD/4K supera facile decine di MB).
  if (file.size > 75 * 1024 * 1024) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`VIDEO_TOO_LARGE (${mb}MB, max 75MB)`);
  }

  // Duration check via <video> + timeout: alcuni container (mov frammentati
  // da iPhone) non emettono loadedmetadata in modo affidabile -> non blocchiamo
  // l'invio se il metadata non arriva entro 8s, accettiamo il file e basta.
  const video = document.createElement('video');
  const url = URL.createObjectURL(file);
  video.src = url;
  video.preload = 'metadata';
  let duration = null;
  try {
    await new Promise((resolve, reject) => {
      const cleanup = () => { clearTimeout(t); video.onloadedmetadata = null; video.onerror = null; };
      const t = setTimeout(() => { cleanup(); resolve(); /* niente errore, prosegui */ }, 8000);
      video.onloadedmetadata = () => { duration = video.duration; cleanup(); resolve(); };
      video.onerror = () => { cleanup(); resolve(); /* non bloccante */ };
    });
  } finally {
    URL.revokeObjectURL(url);
  }
  if (duration != null && Number.isFinite(duration) && duration > MAX_VIDEO_SECONDS) {
    throw new Error(`VIDEO_TOO_LONG (max ${MAX_VIDEO_SECONDS}s, found ${duration.toFixed(1)}s)`);
  }
  return { duration, size: file.size };
}

// =====================================================================
// ENCRYPTION
// =====================================================================

/**
 * Genera chiave random per cifrare un attachment.
 * Returns: Uint8Array (32 bytes)
 */
export function generateAttachmentKey() {
  return sodium.randombytes_buf(sodium.crypto_secretstream_xchacha20poly1305_KEYBYTES);
}

/** Assicura che libsodium sia pronto (necessario prima di generateAttachmentKey). */
export async function ensureSodium() {
  await sodium.ready;
}

/**
 * Cifra un blob a stream (XChaCha20-Poly1305).
 * Returns: { encryptedBlob, header }
 *
 * Il `header` è il chunk iniziale che contiene nonce e setup info.
 * Va incluso nel blob caricato su Storage.
 */
export async function encryptBlobToStream(blob, key) {
  await sodium.ready;
  const arrayBuffer = await blob.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);

  const { state, header } = sodium.crypto_secretstream_xchacha20poly1305_init_push(key);

  // Per blob piccoli (immagini) facciamo un solo push.
  // Per video più grandi potremmo splittare in chunks, ma sotto i 12MB un push singolo va bene.
  const ciphertext = sodium.crypto_secretstream_xchacha20poly1305_push(
    state,
    data,
    null,
    sodium.crypto_secretstream_xchacha20poly1305_TAG_FINAL
  );

  // Combina header + ciphertext
  const combined = new Uint8Array(header.length + ciphertext.length);
  combined.set(header, 0);
  combined.set(ciphertext, header.length);

  return {
    encryptedBlob: new Blob([combined], { type: 'application/octet-stream' }),
    headerLength: header.length,
  };
}

/**
 * Decifra un blob da stream cifrato.
 * Returns: Blob originale
 */
export async function decryptBlobFromStream(encryptedBlob, key, mimeType) {
  await sodium.ready;
  const arrayBuffer = await encryptedBlob.arrayBuffer();
  const combined = new Uint8Array(arrayBuffer);

  const headerLen = sodium.crypto_secretstream_xchacha20poly1305_HEADERBYTES;
  const header = combined.slice(0, headerLen);
  const ciphertext = combined.slice(headerLen);

  const state = sodium.crypto_secretstream_xchacha20poly1305_init_pull(header, key);
  const result = sodium.crypto_secretstream_xchacha20poly1305_pull(state, ciphertext);

  if (!result) {
    throw new Error('DECRYPT_FAILED');
  }
  return new Blob([result.message], { type: mimeType });
}

// =====================================================================
// UPLOAD / DOWNLOAD via signed URL
// =====================================================================

/**
 * Path deterministico dell'allegato cifrato su Storage.
 * Le regole Storage consentono l'accesso ai soli partecipanti della chat
 * (verificato via firestore.get) — niente signed URL, niente IAM signing.
 */
export function attachmentPath(chatId, msgId, kind = 'image') {
  const ext = kind === 'video' ? 'vid.enc'
    : kind === 'audio' ? 'aud.enc'
    : kind === 'file' ? 'file.enc'
    : kind === 'thumbnail' ? 'thumb.enc'
    : 'img.enc';
  return `attachments/${chatId}/${msgId}_${ext}`;
}

/**
 * Carica un blob cifrato su Storage (SDK diretto).
 */
export async function uploadEncryptedBlob(chatId, msgId, encryptedBlob, kind = 'image') {
  const path = attachmentPath(chatId, msgId, kind);
  await uploadBytes(ref(storage, path), encryptedBlob, {
    contentType: 'application/octet-stream',
  });
  return path;
}

/**
 * Scarica un blob cifrato da Storage (SDK diretto).
 */
export async function downloadEncryptedBlob(chatId, msgId, kind = 'image') {
  const path = attachmentPath(chatId, msgId, kind);
  return await getBlob(ref(storage, path));
}
